import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { User } from 'src/app/models/User';
import { Provider } from 'src/app/models/Provider';
import { ProviderReportItem } from 'src/app/models/provider-report.model';
import { ProviderReportService } from 'src/app/shared/services/provider-report.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import { toLocalISODate } from 'src/app/shared/utils';

interface ProviderReportItemWithState extends ProviderReportItem {
  expanded: boolean;
}

@Component({
  selector: 'app-provider-report',
  templateUrl: './provider-report.component.html',
  styleUrls: ['./provider-report.component.scss'],
})
export class ProviderReportComponent implements OnInit, OnDestroy {
  user: User;
  loading = false;

  startDate: Date;
  endDate: Date;
  selectedProviderId: string = null;

  providers: Provider[] = [];
  reportProviders: ProviderReportItemWithState[] = [];
  totalSent = 0;
  activeProvidersCount = 0;
  totalAccountsCount = 0;

  subscriptions: Subscription[] = [];

  get administrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private providerReportService: ProviderReportService,
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getUser();

    if (!this.administrator) {
      this.router.navigate(['/dashboard']);
      return;
    }

    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    this.endDate = today;
    this.startDate = sevenDaysAgo;

    this.loadProviders();
    this.filter();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  loadProviders(): void {
    this.subscriptions.push(
      this.providerReportService.getProviders().subscribe(providers => {
        this.providers = providers;
      })
    );
  }

  filter(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.loading = true;
    this.reportProviders = [];

    this.subscriptions.push(
      this.providerReportService
        .getReport(toLocalISODate(this.startDate), toLocalISODate(this.endDate), this.selectedProviderId)
        .subscribe({
          next: (result) => {
            this.totalSent = result.total_sent;
            this.activeProvidersCount = result.active_providers;
            this.totalAccountsCount = result.total_accounts;
            this.reportProviders = result.providers
              .map(p => ({
                ...p,
                accounts: [...p.accounts].sort((a, b) => b.total_sent - a.total_sent),
                expanded: true,
              }))
              .sort((a, b) => b.total_sent - a.total_sent);
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        })
    );
  }

  clear(): void {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    this.endDate = today;
    this.startDate = sevenDaysAgo;
    this.selectedProviderId = null;
    this.reportProviders = [];
    this.totalSent = 0;
    this.activeProvidersCount = 0;
    this.totalAccountsCount = 0;
  }

  toggleAccordion(provider: ProviderReportItemWithState): void {
    provider.expanded = !provider.expanded;
  }

  formatNumber(value: number): string {
    return (value ?? 0).toLocaleString('pt-BR');
  }

  calcPercent(part: number, total: number): string {
    if (!total) {
      return '0,0%';
    }
    return ((part / total) * 100).toFixed(1).replace('.', ',') + '%';
  }
}
